import { useMcpToolsMetadataQuery } from "@qw/graphql";
import { type MCPPlatform, MCPToolName } from "@qw/qw-common";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Label,
  RadioGroup,
  RadioGroupItem,
  Sheet,
  SheetContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import { useEffect, useMemo, useState } from "react";

import type { ToolFilter } from "../providers/chat-provider";
import { getDefaultToolFilter, setToolFilter as saveToolFilter, setDefaultToolFilter } from "../utils";

type FilterMode = "all" | "whitelist" | "blacklist";

/** Every tool the panel offers. `Elicit` is Alby asking the broker a question, not a tool they grant. */
const ALL_TOOL_NAMES = Object.values(MCPToolName)
  .filter((tool) => tool !== MCPToolName.Elicit)
  .sort((a, b) => a.localeCompare(b));

/**
 * The one-line summary of what a filter allows, for the control that opens the
 * panel. Exported because that control is no longer always the one below — the
 * inbox opens the same panel from the Alby menu's footer.
 */
export const describeToolFilter = (value: ToolFilter | null): string => {
  const total = ALL_TOOL_NAMES.length;

  if (value && "whitelist" in value && value.whitelist != null) {
    const count = value.whitelist.length;
    return count === 0 ? "No tools allowed" : `${count} of ${total} tools allowed`;
  }

  if (value && "blacklist" in value && value.blacklist != null) {
    const count = value.blacklist.length;
    return count === 0 ? `All ${total} tools allowed` : `${total - count} of ${total} tools allowed`;
  }

  return `All ${total} tools allowed`;
};

interface ChatToolFilterSheetProps {
  /**
   * The chat whose filter this edits, or `null` when no chat is in scope — then
   * the panel edits the global default instead, which is the arm of the filter
   * that still means something off a chat.
   */
  chatSessionId: string | null;
  onChange: (filter: ToolFilter | null) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  platform: MCPPlatform;
  value: ToolFilter | null;
}

/**
 * The tool-access panel, open state owned by the caller. Split from
 * {@link ChatToolFilterButton} so the inbox can hoist it out of the Alby menu:
 * a dropdown closes the moment focus enters a dialog, which would unmount a
 * sheet rendered inside it.
 */
export const ChatToolFilterSheet = ({
  chatSessionId,
  onChange,
  onOpenChange,
  open,
  platform,
  value,
}: ChatToolFilterSheetProps) => {
  const isChatScoped = chatSessionId !== null;
  const [formValue, setFormValue] = useState<ToolFilter | null>(value);

  useEffect(() => {
    if (open) {
      setFormValue(value);
    }
  }, [open, value]);

  // Only once the panel is actually open. The inbox mounts this sheet from the
  // nav, which renders on every route, so an unconditional query here would be a
  // request on every page load for a panel most brokers never open. Apollo's
  // cache serves reopens, so the gate costs nothing after the first.
  const { data: toolsData } = useMcpToolsMetadataQuery({
    variables: { input: { platforms: [platform] } },
    skip: !open,
  });

  const mode: FilterMode = useMemo(() => {
    if (formValue === null || formValue === undefined) {
      return "all";
    }
    if ("whitelist" in formValue && formValue.whitelist != null) {
      return "whitelist";
    }
    if ("blacklist" in formValue && formValue.blacklist != null) {
      return "blacklist";
    }
    return "all";
  }, [formValue]);

  const selectedTools = useMemo(() => {
    if (mode === "all") {
      return [];
    }
    if (mode === "whitelist" && formValue && "whitelist" in formValue) {
      return formValue.whitelist || [];
    }
    if (mode === "blacklist" && formValue && "blacklist" in formValue) {
      return formValue.blacklist || [];
    }
    return [];
  }, [mode, formValue]);

  const handleModeChange = (newMode: FilterMode) => {
    const newValue = newMode === "all" ? null : newMode === "whitelist" ? { whitelist: [] } : { blacklist: [] };
    setFormValue(newValue);
  };

  const handleToolToggle = (toolName: string) => {
    if (mode === "all") {
      return;
    }

    const currentTools = new Set(selectedTools);
    if (currentTools.has(toolName)) {
      currentTools.delete(toolName);
    } else {
      currentTools.add(toolName);
    }

    const newTools = Array.from(currentTools);
    const newValue = mode === "whitelist" ? { whitelist: newTools } : { blacklist: newTools };
    setFormValue(newValue);
  };

  const handleDone = () => {
    if (chatSessionId === null) {
      setDefaultToolFilter(formValue);
    } else {
      saveToolFilter(chatSessionId, formValue);
    }
    onChange(formValue);
    onOpenChange(false);
  };

  const [defaultSavedTrigger, setDefaultSavedTrigger] = useState(0);

  const handleSetAsDefault = () => {
    setDefaultToolFilter(formValue);
    setDefaultSavedTrigger((prev) => prev + 1);
  };

  const areFiltersEqual = (a: ToolFilter | null, b: ToolFilter | null): boolean => {
    if (a === null && b === null) {
      return true;
    }
    if (a === null || b === null) {
      return false;
    }

    if ("whitelist" in a && "whitelist" in b) {
      const aList = a.whitelist || [];
      const bList = b.whitelist || [];
      if (aList.length !== bList.length) {
        return false;
      }
      return aList.every((tool) => bList.includes(tool));
    }

    if ("blacklist" in a && "blacklist" in b) {
      const aList = a.blacklist || [];
      const bList = b.blacklist || [];
      if (aList.length !== bList.length) {
        return false;
      }
      return aList.every((tool) => bList.includes(tool));
    }

    return false;
  };

  const isDefaultButtonDisabled = useMemo(() => {
    const savedDefault = getDefaultToolFilter();
    const effectiveDefault = savedDefault ?? null;
    return areFiltersEqual(formValue, effectiveDefault);
  }, [formValue, defaultSavedTrigger]);

  const toolMetadataMap = useMemo(() => {
    const map = new Map<string, { description?: string | null; shortDescription?: string | null }>();
    toolsData?.mcpToolsMetadata?.forEach((tool) => {
      map.set(tool.name, { description: tool.description, shortDescription: tool.shortDescription });
    });
    return map;
  }, [toolsData?.mcpToolsMetadata]);

  const actionButtonLabel = useMemo(() => {
    if (mode === "all") {
      return `✅ Allow all ${ALL_TOOL_NAMES.length} tools`;
    }
    if (mode === "whitelist") {
      return selectedTools.length === 0
        ? "🚫 Allow no tools"
        : `✅ Allow ${selectedTools.length} tool${selectedTools.length !== 1 ? "s" : ""}`;
    }
    if (mode === "blacklist") {
      return selectedTools.length === 0
        ? `✅ Allow all ${ALL_TOOL_NAMES.length} tools`
        : `🔒 Prohibit ${selectedTools.length} tool${selectedTools.length !== 1 ? "s" : ""}`;
    }
    return "Apply";
  }, [mode, selectedTools.length]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-4 overflow-y-auto">
        <div className="space-y-1">
          <h2 className="font-semibold">Tool Access</h2>
          <p className="text-sm text-muted-foreground">
            {isChatScoped
              ? "Choose which MCP tools Alby can use in this chat session."
              : "Choose which MCP tools Alby can use in new chat sessions."}
          </p>
        </div>

        <RadioGroup value={mode} onValueChange={(newMode) => handleModeChange(newMode as FilterMode)}>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="all" id="all" />
            <Label htmlFor="all">Allow all tools</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="whitelist" id="whitelist" />
            <Label htmlFor="whitelist">Only allow selected tools</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="blacklist" id="blacklist" />
            <Label htmlFor="blacklist">Block selected tools</Label>
          </div>
        </RadioGroup>

        {mode !== "all" && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Tools</div>
            <div className="space-y-2">
              {ALL_TOOL_NAMES.map((toolName) => {
                const isSelected = selectedTools.includes(toolName);
                const metadata = toolMetadataMap.get(toolName);

                return (
                  <Collapsible key={toolName} className="rounded-md border p-3">
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        className="mt-0.5"
                        onClick={() => handleToolToggle(toolName)}
                        aria-pressed={isSelected}
                      >
                        <Icon icon={isSelected ? "check_box" : "check_box_outline_blank"} />
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium break-all">{toolName}</div>
                        {metadata?.shortDescription && (
                          <p className="text-sm text-muted-foreground">{metadata.shortDescription}</p>
                        )}
                      </div>
                      {metadata?.description && (
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="xs">
                            Details
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    </div>
                    {metadata?.description && (
                      <CollapsibleContent className="pt-2 text-sm text-muted-foreground">
                        {metadata.description}
                      </CollapsibleContent>
                    )}
                  </Collapsible>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-auto flex flex-wrap gap-2 pt-4">
          {/* Off a chat, Done already writes the default — a second button for the same write would only confuse. */}
          {isChatScoped && (
            <Button variant="outline" onClick={handleSetAsDefault} disabled={isDefaultButtonDisabled}>
              Use for new chats
            </Button>
          )}
          <Button onClick={handleDone}>{actionButtonLabel}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface ChatToolFilterButtonProps {
  chatSessionId: string;
  platform: MCPPlatform;
  value: ToolFilter | null;
  onChange: (filter: ToolFilter | null) => void;
}

/** The tool-access panel behind its own icon trigger. */
export const ChatToolFilterButton = ({ chatSessionId, platform, value, onChange }: ChatToolFilterButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" display="icon" onClick={() => setIsOpen(true)}>
            <Icon icon="build" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{describeToolFilter(value)}</TooltipContent>
      </Tooltip>
      <ChatToolFilterSheet
        chatSessionId={chatSessionId}
        onChange={onChange}
        onOpenChange={setIsOpen}
        open={isOpen}
        platform={platform}
        value={value}
      />
    </>
  );
};
